import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
} from "@mui/material";
import { useEffect, useState } from "react";
import { getScoringBreakdownConfig } from "src/config/leagueConfigs";
import { get } from "src/utils/networkUtils";

const getStats = (key, scoreBreakdown) => {
  const stat = scoreBreakdown.find((item) => item.name === key);
  return stat ? stat.value : 0;
};
const getPoints = (key, scoreBreakdown) => {
  const stats = getStats(key, scoreBreakdown);
  const stat = scoreBreakdown.find((item) => item.name === key);
  return stat ? stat.points * stats : 0;
}
const getTotalPoints = (scoreBreakdown) => {
  return scoreBreakdown.reduce((total, item) => total + (item.points * item.value), 0);
}

export default function ScoreableStageScoringDialog({ onClose, scoreableId, name, sport }) {
  const [stages, setStages] = useState([]);
  const [selectedStage, setSelectedStage] = useState('');
  const [scoringData, setScoringData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scoringLoading, setScoringLoading] = useState(false);

  // Function to convert camelCase scoring keys to readable text
  const formatScoringKeyName = (scoringKey) => {
    return scoringKey
      // Insert space before capital letters
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      // Convert to title case
      .replace(/\b\w/g, l => l.toUpperCase())
      // Strip sport prefixes
      .replace(/Australian Football /g, '')
      .replace(/Rugby /g, '')
      .replace(/Basketball /g, '')
      .replace(/Hockey Team /g, 'Team ')
      // Handle specific hockey terms
      .replace(/Short Handed/g, 'Short-Handed')
      .trim();
  };
  // Fetch stages on component mount
  useEffect(() => {
    setLoading(true);
    get(`/admin/stages`).then((data) => {
      console.log(data);
      setStages(data.stages);
      // find latest stage where start date in past
      const latestStage = data.stages.find((stage) => new Date(stage.endDate) < new Date());
      if (latestStage) {
        setSelectedStage(latestStage.stageId);
      }
    }).catch((error) => {
      console.error("Error fetching stages:", error);
    }).finally(() => {
      setLoading(false);
    });
  }, [scoreableId]);

  // Fetch scoring data when selectedStage changes
  useEffect(() => {
    if (selectedStage) {
      setScoringLoading(true);
      setScoringData(null);
      get(`/admin/scoreables/${scoreableId}/stage-scoring?stageId=${selectedStage}`).then((data) => {
        console.log("Stage scoring data:", data);
        setScoringData(data.scoreBreakdown);
      }).catch((error) => {
        console.error("Error fetching scoring data:", error);
        setScoringData(null);
      }).finally(() => {
        setScoringLoading(false);
      });
    }
  }, [selectedStage, scoreableId]);
  return (
    <Dialog
      open={true}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="alert-dialog-title">Stage Scoring  {name}</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description" sx={{ mb: 2 }}>
          Select a stage to manage scoring:
        </DialogContentText>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ minWidth: 300, mb: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="stage-select-label">Stage</InputLabel>
                <Select
                  labelId="stage-select-label"
                  id="stage-select"
                  value={selectedStage}
                  label="Stage"
                  onChange={(e) => setSelectedStage(e.target.value)}
                >
                  {stages.map((stage) => (
                    <MenuItem key={stage.stageId} value={stage.stageId}>
                      {stage.name || `Stage ${stage.stageId}`}
                      {stage.startDate && ` (${new Date(stage.startDate).toLocaleDateString()})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {scoringLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 100, minWidth: 300 }}>
                <CircularProgress size={30} />
              </Box>
            ) : scoringData && scoringData.length > 0 ? (
              <Box sx={{ minWidth: 300 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell align="center">Stats</TableCell>
                      <TableCell align="center">Points</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getScoringBreakdownConfig(sport).map((scoringKey) => (
                      <TableRow key={scoringKey}>
                        <TableCell>{formatScoringKeyName(scoringKey)}</TableCell>
                        <TableCell align="center">{getStats(scoringKey, scoringData)}</TableCell>
                        <TableCell align="center">{getPoints(scoringKey, scoringData)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow key={"total"}>
                      <TableCell>
                        <strong>Total</strong>
                      </TableCell>
                      <TableCell align="center"></TableCell>
                      <TableCell align="center"><strong>{scoringData ? getTotalPoints(scoringData) : '--'}</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            ) : selectedStage ? (
              <Box sx={{ minWidth: 300, textAlign: 'center', py: 2 }}>
                No scoring data available for this stage
              </Box>
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}
